import { FunctionComponent, createContext, ReactNode, ReactElement } from 'react';
import { AuthUser } from '@/core/types';
import { useAuthenticatedUser, useLogout, useStopSpoof } from '@/queries/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { removeAuthData } from '@/shared/utils/auth';
import Backdrop from '@/components/ui/backdrop';

export interface IAuthContext {
	user?: AuthUser;
	logout: () => void;
	isSpoofing: boolean;
	stopSpoofing: () => void;
	isStoppingSpoofing: boolean;
}

export const AuthContext = createContext<IAuthContext>({
	logout: () => {},
	isSpoofing: false,
	stopSpoofing: () => {},
	isStoppingSpoofing: false,
});

export const AuthProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}): ReactElement => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const { queryKey, query } = useAuthenticatedUser();
	const { data: user } = query;

	const { mutate: logout, isLoading: isLoggingOut } = useLogout({
		onSuccess: () => {
			queryClient.removeQueries({ queryKey });
			removeAuthData();
			navigate('/auth/login', { replace: true });
		},
	});

	const { mutate: stopSpoofing, isLoading: isStoppingSpoofing } = useStopSpoof();

	const isSpoofing = !!user?.spoofedById;

	return (
		<AuthContext.Provider value={{ user, logout, isSpoofing, stopSpoofing, isStoppingSpoofing }}>
			{(isLoggingOut || isStoppingSpoofing) && <Backdrop />}
			{children}
		</AuthContext.Provider>
	);
};
